import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from "@angular/core";
import { ofType, Actions } from "@ngrx/effects";
import { map, take, takeUntil } from "rxjs/operators";
import { ReportsService } from "src/app/shared/services/reports.service";
import {
  InvoiceReportsSearchParams,
  BalanceRow,
} from "src/app/shared/models/invoices.model";
import { ServiceMetaData } from "src/app/shared/models/service-meta.model";
import { PaginationParams } from "src/app/shared/models/common";
import { AuthService } from "src/app/shared/services/auth.service";
import { Store } from "@ngrx/store";
import { Subject } from "rxjs";
import { RUN_REPORTS_SEARCH } from "src/app/shared/ngrx/ngrx-actions";
import {
  balanceSearchMetadata,
  curReportBalanceRows,
  reportsSearchParams,
} from "src/app/shared/ngrx/ngrx-selectors";
import { CommonModule } from "@angular/common";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SpinnerComponent } from "src/app/shared/components/ui/spinner/spinner.component";
import { LoaderComponent } from "src/app/shared/components/ui/loader/loader.component";
import { TableComponent } from "src/app/shared/components/ui/table/table.component";

@Component({
  selector: "balance",
  templateUrl: "./balance.component.html",
  styleUrls: ["./balance.component.sass"],
  standalone: true,
  imports: [CommonModule, LoaderComponent, TableComponent, SpinnerComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BalanceComponent implements OnInit {
  destroy$ = new Subject();
  searchParams: InvoiceReportsSearchParams;
  numberOfRows: number = 10;
  loading: boolean;
  clinicUserSw: boolean;
  statementLoading: boolean;

  @Output() onDocPdfOpen = new EventEmitter<any>();

  // A statement is one clinic's: a LAB user must pick one, and a clinic user
  // with several clinics as well.
  get canPrintStatement(): boolean {
    return !!this.reportsSearchParams?.clinic;
  }

  get reportsSearchParams(): InvoiceReportsSearchParams {
    return this.store.selectSignal(reportsSearchParams)();
  }

  get searchMetaData(): ServiceMetaData {
    return this.store.selectSignal(balanceSearchMetadata)() || {};
  }

  get data(): BalanceRow[] {
    return this.store.selectSignal(curReportBalanceRows)();
  }

  constructor(
    private readonly store: Store,
    private reportsService: ReportsService,
    private authService: AuthService,
    private actions$: Actions,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) {
    this.clinicUserSw = this.authService.currentUser.org == "CLINIC";

    this.actions$
      .pipe(
        ofType(RUN_REPORTS_SEARCH),
        takeUntil(this.destroy$),
        map(({}) => {
          this.runSearch(true);
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    if (this.store.selectSignal(reportsSearchParams)()) {
      this.runSearch(true);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(1);
  }

  runSearch(newSearch: boolean) {
    if (newSearch) {
      this.searchParams = {
        rangeTo: this.numberOfRows,
        rangeFrom: 1,
        searchId: null,
      };
    }

    this.searchParams.clinic = this.reportsSearchParams.clinic;
    this.searchParams.documentId = this.reportsSearchParams.documentId;
    this.searchParams.fromDate = this.reportsSearchParams.fromDate;
    this.searchParams.toDate = this.reportsSearchParams.toDate;

    this.loading = true;
    this.cdr.markForCheck();
    this.reportsService.getClientBalance(this.searchParams).subscribe(
      () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
      () => {
        this.loading = false;
        this.cdr.markForCheck();
      }
    );
  }

  loadDataOnPagination(event: PaginationParams) {
    if (
      !this.data ||
      (event.rangeFrom == this.searchParams.rangeFrom &&
        event.rangeTo == this.searchParams.rangeTo)
    )
      return;

    this.searchParams.rangeFrom = event.rangeFrom;
    this.searchParams.rangeTo = event.rangeTo;
    this.searchParams.searchId = this.searchMetaData.searchId;

    this.runSearch(false);
  }

  printStatement() {
    const params = this.reportsSearchParams;
    if (!params?.clinic) return;

    this.statementLoading = true;
    this.cdr.markForCheck();
    this.reportsService
      .getBalancePdfUrl(params.clinic.id, params.fromDate, params.toDate)
      .subscribe(
        (url) => {
          this.statementLoading = false;
          this.cdr.markForCheck();
          if (url) {
            this.onDocPdfOpen.emit({
              docTitle: `${this.translate.instant("translate.reports.statement")} - ${params.clinic.text}`,
              url,
            });
          }
        },
        () => {
          this.statementLoading = false;
          this.cdr.markForCheck();
        }
      );
  }
}
